import { useEffect, useRef, useState } from "react";
import { useLocale, useT, type Locale } from "@/lib/i18n";
import { pathForLocale } from "@/lib/locale-url";

const LABELS: Record<Locale, { code: string; name: string }> = {
  nl: { code: "NL", name: "Nederlands" },
  en: { code: "EN", name: "English" },
};

/**
 * Taalkiezer (NL/EN). `tone` past de stijl aan: "header" (knop op de witte balk) of
 * "footer" (tekstlink). Zet de taal sitebreed via de i18n-store.
 */
/** Hoogte van het opengeklapte lijstje (2 talen + randen) — genoeg om te bepalen of het past. */
const MENU_H = 104;

export function LanguageSwitcher({ tone = "header", className = "" }: { tone?: "header" | "footer"; className?: string }) {
  const t = useT();
  const [locale, setLocale] = useLocale();
  const [open, setOpen] = useState(false);
  // Naar boven openen wanneer het lijstje anders onder de onderkant van het scherm valt.
  // In het mobiele menu staat de taalknop onderin beeld — daar viel het lijstje buiten
  // het scherm en was de keuze niet te zien. De footer-variant klapt altijd omhoog.
  const [dropUp, setDropUp] = useState(tone === "footer");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Richting bepalen op het moment van openen (niet bij het renderen van de knop): pas dan
  // weten we waar de knop écht staat, en een sticky header/menu kan intussen verschoven zijn.
  const toggle = (): void => {
    setOpen((wasOpen) => {
      if (!wasOpen && tone !== "footer" && ref.current) {
        const r = ref.current.getBoundingClientRect();
        const ruimteOnder = window.innerHeight - r.bottom;
        setDropUp(ruimteOnder < MENU_H && r.top > ruimteOnder);
      }
      return !wasOpen;
    });
  };

  const trigger =
    tone === "header"
      ? "inline-flex h-11 items-center gap-2 px-3 text-sm font-medium text-primary-700 transition hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      : "inline-flex items-center gap-1.5 text-primary-600 transition hover:text-primary-900";

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button type="button" onClick={toggle} aria-expanded={open} aria-label={`${tone === "header" ? LABELS[locale].code : LABELS[locale].name} — ${t("taal wijzigen")}`} className={trigger}>
        {tone === "footer" && (
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
          </svg>
        )}
        <span className={tone === "header" ? "text-xs font-bold tracking-wide" : ""}>
          {tone === "header" ? LABELS[locale].code : LABELS[locale].name}
        </span>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" className={open ? "rotate-180 transition" : "transition"}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className={`absolute right-0 z-50 w-40 border border-primary-100 bg-white p-1 text-sm shadow-xl ${dropUp ? "bottom-full mb-2" : "mt-2"}`}>
          {(["nl", "en"] as Locale[]).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => {
                setLocale(l);
                setOpen(false);
                // Taal zit sinds 01-08-2026 IN de URL (/en/...): schakelen is dus navigeren, niet
                // alleen een voorkeur zetten. Volledige navigatie (geen router-push) omdat we van
                // basename wisselen — die staat vast bij het opstarten van de router.
                const doel = pathForLocale(window.location.pathname, l) + window.location.search + window.location.hash;
                if (doel !== window.location.pathname + window.location.search + window.location.hash) {
                  window.location.assign(doel);
                }
              }}
              className={`flex w-full items-center justify-between px-3 py-2 text-left font-medium transition hover:bg-primary-50 ${l === locale ? "text-primary-900" : "text-primary-600"}`}
            >
              <span>{LABELS[l].name}</span>
              {l === locale && (
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-accent-700">
                  <path d="M5 12l5 5L20 6" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
